class ShareTheLook {
  constructor(container) {
    this.container = container;
    this.searchInput = container.querySelector('.js-stl-search-input');
    this.resultsContainer = container.querySelector('.js-stl-results');
    this.selectedContainer = container.querySelector('.js-stl-selected-list');
    this.copyBtn = container.querySelector('.js-stl-copy-btn');
    this.sharedViewContainer = container.querySelector('.js-stl-shared-view');
    this.creationViewContainer = container.querySelector(
      '.js-stl-creation-view'
    );
    this.blankState = container.querySelector('.js-stl-blank-state'); // Legacy, might be null now
    this.initialState = container.querySelector('.js-stl-initial-state');
    this.searchState = container.querySelector('.js-stl-search-state'); // Legacy wrapper, now we target results directly?
    this.countSpan = container.querySelector('.js-stl-count');

    this.storefrontToken = container.dataset.storefrontToken;
    this.shopUrl = container.dataset.shopUrl;
    this.country = container.dataset.country || 'US'; // Default fallback
    this.language = container.dataset.language || 'EN'; // Default fallback

    this.state = {
      searchQuery: '',
      searchResults: [],
      selectedProducts: [],
      isSharedView: false,
      isSearchActive: false,
      isLoading: false,
    };

    this.mobileToggle = container.querySelector('.js-stl-mobile-toggle');
    this.mobileCountSpan = container.querySelector('.js-stl-count-mobile');
    this.sidebar = container.querySelector('.stl-sidebar');
    this.lookNameInput = container.querySelector('.js-stl-look-name-input');

    // Track marketplace context to detect changes
    this.currentMarketplaceContext = this.getMarketplaceContext();

    this.init();

    // Trigger fade-in
    requestAnimationFrame(() => {
      this.container.classList.add('loaded');
    });

    // Listen for marketplace/region changes
    this.setupMarketplaceChangeListener();
  }

  getMarketplaceContext() {
    // Get marketplace context from country code (primary indicator)
    // This helps detect when marketplace/region changes
    try {
      // Use country code from data attribute (set by Liquid from localization)
      // Fallback to checking URL params or cookies
      const urlParams = new URLSearchParams(window.location.search);
      return (
        this.country ||
        urlParams.get('country') ||
        urlParams.get('marketplace') ||
        urlParams.get('market') ||
        document.cookie.match(/marketplace=([^;]+)/)?.[1] ||
        document.cookie.match(/market=([^;]+)/)?.[1] ||
        window.location.hostname
      );
    } catch (e) {
      return this.country || window.location.hostname;
    }
  }

  setupMarketplaceChangeListener() {
    // Clear search results when page becomes visible (marketplace switch might cause reload)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible - check if marketplace context changed
        const newContext = this.getMarketplaceContext();
        if (newContext !== this.currentMarketplaceContext) {
          this.currentMarketplaceContext = newContext;
          this.clearSearchResults();
        }
      }
    });

    // Also listen for focus events (user might switch marketplace in another tab)
    window.addEventListener('focus', () => {
      const newContext = this.getMarketplaceContext();
      if (newContext !== this.currentMarketplaceContext) {
        this.currentMarketplaceContext = newContext;
        this.clearSearchResults();
      }
    });

    // Listen for popstate (back/forward navigation might change marketplace)
    window.addEventListener('popstate', () => {
      const newContext = this.getMarketplaceContext();
      if (newContext !== this.currentMarketplaceContext) {
        this.currentMarketplaceContext = newContext;
        this.clearSearchResults();
      }
    });
  }

  clearSearchResults() {
    // Clear search results and reset search state when marketplace changes
    if (this.state.searchQuery || this.state.searchResults.length > 0) {
      this.state.searchQuery = '';
      this.state.searchResults = [];
      this.state.isSearchActive = false;
      if (this.searchInput) {
        this.searchInput.value = '';
      }
      this.render();
    }
  }

  init() {
    // Check URL for products
    const urlParams = new URLSearchParams(window.location.search);
    const productsParam = urlParams.get('products');

    if (productsParam) {
      this.state.isSharedView = true;
      this.render();
      this.fetchSharedProducts(productsParam.split(','));
    } else {
      this.addEventListeners();
      this.render();
    }
  }

  addEventListeners() {
    if (this.searchInput) {
      this.searchInput.addEventListener('focus', () => {
        if (!this.state.isSearchActive) {
          this.state.isSearchActive = true;
          this.render();
        }
      });

      this.searchInput.addEventListener(
        'input',
        this.debounce((e) => {
          this.handleSearch(e.target.value);
        }, 500)
      );
    }

    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', (e) => this.copyLink(e));
    }

    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const rightColumn = this.container.querySelector('.stl-right-column');

        // Ensure sidebar is visible first if there are selected products
        if (this.state.selectedProducts.length > 0) {
          if (rightColumn && !rightColumn.classList.contains('is-visible')) {
            rightColumn.classList.add('is-visible');
          }

          // Toggle expansion after a brief delay to ensure visibility transition
          setTimeout(() => {
            if (this.sidebar && rightColumn) {
              const isExpanding =
                !this.sidebar.classList.contains('is-expanded');
              this.sidebar.classList.toggle('is-expanded');

              // Add class to right column for mobile expansion
              if (isExpanding) {
                rightColumn.classList.add('is-expanded-mobile');
              } else {
                rightColumn.classList.remove('is-expanded-mobile');
              }

              const icon = this.mobileToggle.querySelector(
                '.stl-mobile-toggle-icon'
              );
              if (icon) {
                icon.textContent = this.sidebar.classList.contains(
                  'is-expanded'
                )
                  ? '▼'
                  : '▲';
              }
            }
          }, 100);
        }
      });
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  async handleSearch(query) {
    // Check if marketplace context has changed before searching
    const newContext = this.getMarketplaceContext();
    if (newContext !== this.currentMarketplaceContext) {
      this.currentMarketplaceContext = newContext;
      // Refresh country and language from data attributes in case they've changed
      this.country = this.container.dataset.country || this.country || 'US';
      this.language = this.container.dataset.language || this.language || 'EN';
      // Clear any existing results from previous marketplace
      this.state.searchResults = [];
    }

    this.state.searchQuery = query;

    if (!this.state.isSearchActive) {
      this.state.isSearchActive = true;
    }

    if (query.length < 3) {
      this.state.searchResults = [];
      this.render();
      return;
    }

    this.state.isLoading = true;
    this.render();

    try {
      const products = await this.searchProducts(query);
      this.state.searchResults = products;
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      this.state.isLoading = false;
      this.render();
    }
  }

  async searchProducts(query, limit = 50) {
    // Use 'search' query with @inContext to respect market/region catalogs
    // Shopify will automatically filter products based on the market context
    const graphqlQuery = `
      query searchProducts($query: String!, $first: Int!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
        search(query: $query, first: $first, types: PRODUCT) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                productType
                availableForSale
                featuredImage {
                  url(transform: {maxWidth: 400})
                  altText
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                compareAtPriceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                options {
                  name
                  values
                }
                variants(first: 20) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    if (!this.storefrontToken) {
      console.error(
        'Share the Look: Storefront Access Token is missing. Please add it in the section settings.'
      );
      return [];
    }

    // Use relative path to avoid CORS issues and ensure we hit the current domain
    // Include credentials to ensure marketplace context cookies are sent
    const response = await fetch(`/api/2023-10/graphql.json`, {
      method: 'POST',
      credentials: 'include', // Include cookies to respect current marketplace context
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken,
      },
      body: JSON.stringify({
        query: graphqlQuery,
        variables: {
          query,
          first: limit,
          country: this.country,
          language: this.language,
        },
      }),
    });

    const { data, errors } = await response.json();

    if (errors) {
      console.error('GraphQL Errors:', errors);
    }

    // Update to use search.edges instead of products.edges and filter out null nodes
    // Products are automatically filtered by Shopify based on @inContext market
    const products =
      data?.search?.edges
        .map((edge) => edge.node)
        .filter((node) => node !== null) || [];

    return products;
  }

  async fetchProductsByHandles(handles) {
    if (!this.storefrontToken) {
      console.error('Share the Look: Storefront Access Token is missing.');
      return [];
    }

    // Construct aliased query to fetch specific products by handle
    // product0: product(handle: "handle-0") { ... }
    const aliases = handles
      .map(
        (handle, index) => `
      product${index}: product(handle: "${handle}") {
        id
        title
        handle
        productType
        featuredImage {
          url(transform: {maxWidth: 400})
          altText
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        options {
          name
          values
        }
        variants(first: 20) {
          edges {
            node {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
            }
          }
        }
      }
    `
      )
      .join('\n');

    const graphqlQuery = `
      query getProductsByHandles($country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
        ${aliases}
      }
    `;

    try {
      const response = await fetch(`/api/2023-10/graphql.json`, {
        method: 'POST',
        credentials: 'include', // Include cookies to respect current marketplace context
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: {
            country: this.country,
            language: this.language,
          },
        }),
      });

      const { data, errors } = await response.json();

      if (errors) {
        console.error('GraphQL Errors:', errors);
      }

      if (!data) return [];

      // Convert object response { product0: {...}, product1: {...} } back to array
      // Filter out nulls (invalid handles)
      return Object.values(data).filter((p) => p !== null);
    } catch (error) {
      console.error('Error fetching products by handles:', error);
      return [];
    }
  }

  async fetchSharedProducts(handles) {
    this.state.isLoading = true;
    // Show loading state
    const existingHeading =
      this.container.querySelector('.stl-main-heading')?.textContent ||
      'Share the Look';
    this.container.innerHTML = `
      <div class="share-the-look-container">
        <div class="stl-header-container">
          <h2 class="stl-main-heading">${existingHeading}</h2>
        </div>
        <div class="stl-loading">
          <div class="stl-loader"></div>
          <div class="stl-loading-text">Loading your look...</div>
        </div>
      </div>
    `;

    try {
      // Use GraphQL to fetch products by handles - @inContext automatically filters by marketplace
      // Products not in the current market won't be returned by Shopify
      const products = await this.fetchProductsByHandles(handles);

      // Filter products to only include those with at least one available variant
      // (Products in market but with no available variants should be filtered out)
      const availableProducts = products.filter((product) => {
        if (!product || !product.variants || !product.variants.edges) {
          return false;
        }
        return product.variants.edges.some(
          (edge) => edge.node && edge.node.availableForSale === true
        );
      });

      if (availableProducts.length === 0) {
        this.container.innerHTML = `
          <div class="share-the-look-container">
            <div class="stl-header-container">
              <h2 class="stl-main-heading">${existingHeading}</h2>
            </div>
            <div class="stl-empty-state">
              <div class="stl-empty-state-text">No products available in your marketplace</div>
            </div>
          </div>
        `;
        return;
      }

      // Fetch rendered product cards for each available product
      const cardPromises = availableProducts.map(async (product) => {
        try {
          const response = await fetch(`/products/${product.handle}?view=card`);
          if (!response.ok) {
            console.warn(
              `Failed to fetch card for product ${product.handle}:`,
              response.status
            );
            return null;
          }
          const html = await response.text();
          return html.trim();
        } catch (error) {
          console.warn(
            `Error fetching card for product ${product.handle}:`,
            error
          );
          return null;
        }
      });

      const cardHtmls = await Promise.all(cardPromises);
      const validCards = cardHtmls.filter((html) => html !== null);

      // Check for title param and update heading
      const params = new URLSearchParams(window.location.search);
      const title = params.get('title');
      const displayHeading = title
        ? decodeURIComponent(title)
        : existingHeading;

      // Render the products grid
      this.container.innerHTML = `
        <div class="share-the-look-container">
          <div class="stl-header-container">
            <h2 class="stl-main-heading">${displayHeading}</h2>
          </div>
          <div class="collection__grid js-stl-shared-grid" data-cols="4">
            ${validCards.join('')}
          </div>
        </div>
      `;
    } catch (error) {
      console.error('Error fetching shared products:', error);
      this.container.innerHTML = `
        <div class="share-the-look-container">
          <div class="stl-header-container">
            <h2 class="stl-main-heading">${existingHeading}</h2>
          </div>
          <div class="stl-empty-state">Error loading products. Please try again.</div>
        </div>
      `;
    } finally {
      this.state.isLoading = false;
    }
  }

  toggleProductSelection(product) {
    const index = this.state.selectedProducts.findIndex(
      (p) => p.id === product.id
    );

    if (index === -1) {
      // Add product
      this.state.selectedProducts.push(product);
      this.addProductToList(product);
    } else {
      // Remove product
      this.state.selectedProducts.splice(index, 1);
      this.removeProductFromList(product.id);
    }

    // Update count
    if (this.countSpan) {
      this.countSpan.textContent = this.state.selectedProducts.length;
    }
    if (this.mobileCountSpan) {
      this.mobileCountSpan.textContent = this.state.selectedProducts.length;
    }

    // Show/hide sidebar based on selection
    this.updateSidebarVisibility();

    // Update card selection state
    const card = this.resultsContainer.querySelector(
      `.stl-product-card[data-id="${product.id}"]`
    );
    if (card) {
      card.classList.toggle('selected');
    }
  }

  updateSidebarVisibility() {
    const sidebar = this.container.querySelector('.stl-right-column');
    if (sidebar) {
      if (this.state.selectedProducts.length > 0) {
        sidebar.classList.add('is-visible');
      } else {
        sidebar.classList.remove('is-visible');
        sidebar.classList.remove('is-expanded-mobile');
      }
    }
  }

  addProductToList(product) {
    // Remove empty state if present
    const emptyState = this.selectedContainer.querySelector('.stl-empty-state');
    if (emptyState) {
      emptyState.remove();
    }

    const itemHtml = `
      <div class="stl-selected-item" data-id="${product.id}">
        <img src="${product.featuredImage?.url || ''}" alt="${product.title}">
        <div class="stl-selected-info">
          <div class="stl-selected-title">${product.title}</div>
        </div>
        <button class="stl-remove-btn" data-id="${product.id}">×</button>
      </div>
    `;

    // Create element from HTML string
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = itemHtml.trim();
    const newItem = tempDiv.firstChild;

    // Add event listener
    newItem.querySelector('.stl-remove-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleProductSelection(product);
    });

    this.selectedContainer.appendChild(newItem);

    // Scroll to bottom
    this.selectedContainer.scrollTop = this.selectedContainer.scrollHeight;
  }

  removeProductFromList(productId) {
    const item = this.selectedContainer.querySelector(
      `.stl-selected-item[data-id="${productId}"]`
    );
    if (item) {
      item.classList.add('removing');
      item.addEventListener('animationend', () => {
        item.remove();
        if (this.state.selectedProducts.length === 0) {
          this.selectedContainer.innerHTML =
            '<div class="stl-empty-state">No products selected</div>';
        }
        // Update sidebar visibility after removal
        this.updateSidebarVisibility();
      });
    }
  }

  createConfetti(x, y) {
    const colors = [
      '#000000',
      '#ffffff',
      '#ff0000',
      '#00ff00',
      '#0000ff',
      '#ffff00',
      '#ff00ff',
      '#00ffff',
    ];
    const particleCount = 50;

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() * 8 + 4;
      const angle =
        (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.5;
      const velocity = Math.random() * 300 + 200;
      const lifetime = 2000; // 2 seconds

      particle.style.position = 'fixed';
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.backgroundColor = color;
      particle.style.borderRadius = '50%';
      particle.style.pointerEvents = 'none';
      particle.style.zIndex = '10000';
      particle.style.transform = 'translate(-50%, -50%)';
      particle.style.opacity = '1';
      particle.style.willChange = 'transform, opacity';

      document.body.appendChild(particle);

      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;
      const gravity = 500;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = (currentTime - startTime) / 1000;
        const currentX = x + vx * elapsed;
        const currentY = y + vy * elapsed + (gravity * elapsed * elapsed) / 2;
        const opacity = Math.max(0, 1 - elapsed / (lifetime / 1000));

        if (opacity > 0 && elapsed < lifetime / 1000) {
          particle.style.left = `${currentX}px`;
          particle.style.top = `${currentY}px`;
          particle.style.opacity = opacity;
          requestAnimationFrame(animate);
        } else {
          if (particle.parentNode) {
            particle.remove();
          }
        }
      };

      requestAnimationFrame(animate);
    }
  }

  copyLink(event) {
    const handles = this.state.selectedProducts.map((p) => p.handle).join(',');
    let url = `${window.location.origin}${window.location.pathname}?products=${handles}`;

    // Add title if present
    if (this.lookNameInput && this.lookNameInput.value.trim()) {
      url += `&title=${encodeURIComponent(this.lookNameInput.value.trim())}`;
    }

    // Get click position for confetti - trigger immediately on click
    const clickX = event
      ? event.clientX
      : this.copyBtn
      ? this.copyBtn.getBoundingClientRect().left + this.copyBtn.offsetWidth / 2
      : window.innerWidth / 2;
    const clickY = event
      ? event.clientY
      : this.copyBtn
      ? this.copyBtn.getBoundingClientRect().top + this.copyBtn.offsetHeight / 2
      : window.innerHeight / 2;

    // Trigger confetti immediately
    this.createConfetti(clickX, clickY);

    const successCallback = () => {
      const originalText = this.copyBtn.innerHTML;
      this.copyBtn.innerHTML = 'Copied!';
      setTimeout(() => {
        this.copyBtn.innerHTML = originalText;
      }, 1000);
    };

    // Try Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(successCallback)
        .catch(() => this.fallbackCopyTextToClipboard(url, successCallback));
    } else {
      this.fallbackCopyTextToClipboard(url, successCallback);
    }
  }

  fallbackCopyTextToClipboard(text, onSuccess) {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Ensure it's not visible but part of DOM
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);

    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        onSuccess();
      } else {
        console.error('Fallback copy failed.');
      }
    } catch (err) {
      console.error('Fallback copy error', err);
    }

    document.body.removeChild(textArea);
  }

  render() {
    if (this.state.isSharedView) {
      this.creationViewContainer.style.display = 'none';
      this.sharedViewContainer.style.display = 'block';
      // renderSharedView is now handled by fetchSharedProducts replacing innerHTML
    } else {
      this.creationViewContainer.style.display = 'block';
      this.sharedViewContainer.style.display = 'none';
      this.renderCreationView();
    }
  }

  renderCreationView() {
    // Toggle Initial State vs Results
    // If we have a search query, show results container (even if empty/loading)
    // If no search query, show initial state

    if (this.state.searchQuery) {
      if (this.initialState) this.initialState.style.display = 'none';
      if (this.resultsContainer) this.resultsContainer.style.display = 'grid'; // or block/flex depending on CSS
    } else {
      if (this.initialState) this.initialState.style.display = 'block';
      if (this.resultsContainer) this.resultsContainer.style.display = 'none';
    }

    // Update Count
    if (this.countSpan) {
      this.countSpan.textContent = this.state.selectedProducts.length;
    }
    if (this.mobileCountSpan) {
      this.mobileCountSpan.textContent = this.state.selectedProducts.length;
    }

    // Update sidebar visibility
    this.updateSidebarVisibility();

    // Render Search Results
    if (this.state.isLoading) {
      if (this.resultsContainer) {
        this.resultsContainer.style.display = 'flex';
        this.resultsContainer.innerHTML = `
          <div class="stl-loading">
            <div class="stl-loader"></div>
            <div class="stl-loading-text">Loading products...</div>
          </div>
        `;
      }
    } else if (
      this.state.searchQuery &&
      this.state.searchResults.length === 0
    ) {
      this.resultsContainer.innerHTML = `
        <div class="stl-empty-state">
          <div class="stl-empty-state-text">No products found</div>
          <div class="stl-empty-state-subtext">Try searching with different keywords</div>
        </div>
      `;
    } else if (this.state.searchResults.length > 0) {
      this.resultsContainer.innerHTML = this.state.searchResults
        .map((product) => this.createProductCard(product))
        .join('');

      // Re-attach listeners
      this.resultsContainer
        .querySelectorAll('.stl-product-card')
        .forEach((card) => {
          card.addEventListener('click', (e) => {
            // Ignore clicks on quick add buttons or links
            if (
              e.target.closest('.product-card__quick-add-size-btn') ||
              e.target.closest('a')
            )
              return;

            const product = this.state.searchResults.find(
              (p) => p.id === card.dataset.id
            );
            this.toggleProductSelection(product);
          });
        });
    }

    // Initial Render of Selected List (only if empty, subsequent updates handled by add/remove methods)
    if (
      this.selectedContainer.children.length === 0 ||
      (this.selectedContainer.children.length === 1 &&
        this.selectedContainer.querySelector('.stl-empty-state'))
    ) {
      if (this.state.selectedProducts.length > 0) {
        this.selectedContainer.innerHTML = ''; // Clear empty state
        this.state.selectedProducts.forEach((product) =>
          this.addProductToList(product)
        );
      } else {
        this.selectedContainer.innerHTML =
          '<div class="stl-empty-state">No products selected</div>';
      }
    }
  }

  // Convert currency code to symbol
  getCurrencySymbol(currencyCode) {
    const currencyMap = {
      USD: '$',
      GBP: '£',
      EUR: '€',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
      CHF: 'CHF',
      CNY: '¥',
      INR: '₹',
      BRL: 'R$',
      MXN: '$',
      KRW: '₩',
      SGD: 'S$',
      HKD: 'HK$',
      NZD: 'NZ$',
      ZAR: 'R',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      TRY: '₺',
      RUB: '₽',
      AED: 'د.إ',
      SAR: '﷼',
      ILS: '₪',
      THB: '฿',
      MYR: 'RM',
      PHP: '₱',
      IDR: 'Rp',
      VND: '₫',
      CZK: 'Kč',
      HUF: 'Ft',
      RON: 'lei',
      BGN: 'лв',
      HRK: 'kn',
      RSD: 'дин',
      UAH: '₴',
      EGP: 'E£',
      NGN: '₦',
      KES: 'KSh',
      GHS: '₵',
      PKR: '₨',
      BDT: '৳',
      LKR: 'Rs',
      NPR: 'Rs',
      MMK: 'K',
      KHR: '៛',
      LAK: '₭',
      MOP: 'P',
      TWD: 'NT$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
      ARS: '$',
      UYU: '$U',
      BOB: 'Bs.',
      PYG: '₲',
      BHD: '.د.ب',
      OMR: '﷼',
      JOD: 'JD',
      LBP: '£',
      KWD: 'د.ك',
      QAR: '﷼',
      BND: 'B$',
      FJD: 'FJ$',
      PGK: 'K',
      SBD: 'SI$',
      VUV: 'Vt',
      WST: 'WS$',
      XPF: 'F',
      TOP: 'T$',
      SZL: 'E',
      LSL: 'L',
      BWP: 'P',
      NAD: 'N$',
      MZN: 'MT',
      AOA: 'Kz',
      MWK: 'MK',
      TZS: 'TSh',
      UGX: 'USh',
      RWF: 'RF',
      ETB: 'Br',
      DJF: 'Fdj',
      SOS: 'S',
      ERN: 'Nfk',
      SDG: 'ج.س.',
      SSP: '£',
      LYD: 'ل.د',
      TND: 'د.ت',
      DZD: 'د.ج',
      MAD: 'د.م.',
      MUR: '₨',
      SCR: 'SR',
      MVR: 'Rf',
      AFN: '؋',
      ALL: 'Lek',
      AMD: '֏',
      AZN: '₼',
      BYN: 'Br',
      BAM: 'КМ',
      GEL: '₾',
      ISK: 'kr',
      MDL: 'L',
      MKD: 'ден',
      TMT: 'm',
      XOF: 'CFA',
      XAF: 'FCFA',
      KZT: '₸',
      KGS: 'сом',
      TJS: 'ЅМ',
      UZS: 'лв',
      MNT: '₮',
      BTN: 'Nu.',
      MOP: 'P',
      BIF: 'Fr',
      CDF: 'FC',
      GMD: 'D',
      GNF: 'Fr',
      LRD: 'L$',
      SLL: 'Le',
      STN: 'Db',
      XCD: '$',
      AWG: 'ƒ',
      BBD: '$',
      BMD: '$',
      BSD: '$',
      BZD: '$',
      KYD: '$',
      JMD: '$',
      TTD: 'TT$',
      GYD: '$',
      SRD: '$',
      BRL: 'R$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
      ARS: '$',
      UYU: '$U',
      BOB: 'Bs.',
      PYG: '₲',
      VES: 'Bs.',
      DOP: 'RD$',
      HTG: 'G',
      JMD: '$',
      CUP: '₱',
      XCD: '$',
      ANG: 'ƒ',
      SRD: '$',
      FKP: '£',
      GGP: '£',
      GIP: '£',
      JEP: '£',
      IMP: '£',
      SHP: '£',
      GBP: '£',
      EUR: '€',
      CHF: 'CHF',
      ISK: 'kr',
      NOK: 'kr',
      SEK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      RON: 'lei',
      BGN: 'лв',
      HRK: 'kn',
      RSD: 'дин',
      BAM: 'КМ',
      MKD: 'ден',
      ALL: 'Lek',
      AMD: '֏',
      AZN: '₼',
      BYN: 'Br',
      GEL: '₾',
      MDL: 'L',
      TMT: 'm',
      KZT: '₸',
      KGS: 'сом',
      TJS: 'ЅМ',
      UZS: 'лв',
      MNT: '₮',
      RUB: '₽',
      UAH: '₴',
      TRY: '₺',
      ILS: '₪',
      JOD: 'JD',
      LBP: '£',
      SYP: '£',
      IQD: 'ع.د',
      IRR: '﷼',
      AFN: '؋',
      PKR: '₨',
      INR: '₹',
      BDT: '৳',
      LKR: 'Rs',
      NPR: 'Rs',
      BTN: 'Nu.',
      MMK: 'K',
      THB: '฿',
      LAK: '₭',
      KHR: '៛',
      VND: '₫',
      CNY: '¥',
      JPY: '¥',
      KRW: '₩',
      TWD: 'NT$',
      HKD: 'HK$',
      MOP: 'P',
      SGD: 'S$',
      MYR: 'RM',
      IDR: 'Rp',
      PHP: '₱',
      BND: 'B$',
      XOF: 'CFA',
      XAF: 'FCFA',
      XPF: 'F',
      AUD: 'A$',
      NZD: 'NZ$',
      FJD: 'FJ$',
      PGK: 'K',
      SBD: 'SI$',
      VUV: 'Vt',
      WST: 'WS$',
      TOP: 'T$',
      XCD: '$',
      AWG: 'ƒ',
      BBD: '$',
      BMD: '$',
      BSD: '$',
      BZD: '$',
      KYD: '$',
      JMD: '$',
      TTD: 'TT$',
      GYD: '$',
      SRD: '$',
      ANG: 'ƒ',
      BRL: 'R$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
      ARS: '$',
      UYU: '$U',
      BOB: 'Bs.',
      PYG: '₲',
      VES: 'Bs.',
      DOP: 'RD$',
      HTG: 'G',
      CUP: '₱',
      FKP: '£',
      GGP: '£',
      GIP: '£',
      JEP: '£',
      IMP: '£',
      SHP: '£',
      GBP: '£',
      EUR: '€',
      CHF: 'CHF',
      ISK: 'kr',
      NOK: 'kr',
      SEK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      RON: 'lei',
      BGN: 'лв',
      HRK: 'kn',
      RSD: 'дин',
      BAM: 'КМ',
      MKD: 'ден',
      ALL: 'Lek',
      AMD: '֏',
      AZN: '₼',
      BYN: 'Br',
      GEL: '₾',
      MDL: 'L',
      TMT: 'm',
      KZT: '₸',
      KGS: 'сом',
      TJS: 'ЅМ',
      UZS: 'лв',
      MNT: '₮',
      RUB: '₽',
      UAH: '₴',
      TRY: '₺',
      ILS: '₪',
      JOD: 'JD',
      LBP: '£',
      SYP: '£',
      IQD: 'ع.د',
      IRR: '﷼',
      AFN: '؋',
      PKR: '₨',
      INR: '₹',
      BDT: '৳',
      LKR: 'Rs',
      NPR: 'Rs',
      BTN: 'Nu.',
      MMK: 'K',
      THB: '฿',
      LAK: '₭',
      KHR: '៛',
      VND: '₫',
      CNY: '¥',
      JPY: '¥',
      KRW: '₩',
      TWD: 'NT$',
      HKD: 'HK$',
      MOP: 'P',
      SGD: 'S$',
      MYR: 'RM',
      IDR: 'Rp',
      PHP: '₱',
      BND: 'B$',
      XOF: 'CFA',
      XAF: 'FCFA',
      XPF: 'F',
      AUD: 'A$',
      NZD: 'NZ$',
      FJD: 'FJ$',
      PGK: 'K',
      SBD: 'SI$',
      VUV: 'Vt',
      WST: 'WS$',
      TOP: 'T$',
      ZAR: 'R',
      SZL: 'E',
      LSL: 'L',
      BWP: 'P',
      NAD: 'N$',
      MZN: 'MT',
      AOA: 'Kz',
      MWK: 'MK',
      TZS: 'TSh',
      UGX: 'USh',
      RWF: 'RF',
      ETB: 'Br',
      DJF: 'Fdj',
      SOS: 'S',
      ERN: 'Nfk',
      SDG: 'ج.س.',
      SSP: '£',
      LYD: 'ل.د',
      TND: 'د.ت',
      DZD: 'د.ج',
      MAD: 'د.م.',
      MUR: '₨',
      SCR: 'SR',
      MVR: 'Rf',
      BIF: 'Fr',
      CDF: 'FC',
      GMD: 'D',
      GNF: 'Fr',
      LRD: 'L$',
      SLL: 'Le',
      STN: 'Db',
      EGP: 'E£',
      NGN: '₦',
      KES: 'KSh',
      GHS: '₵',
      AED: 'د.إ',
      SAR: '﷼',
      OMR: '﷼',
      BHD: '.د.ب',
      KWD: 'د.ك',
      QAR: '﷼',
      YER: '﷼',
      BND: 'B$',
      FJD: 'FJ$',
      PGK: 'K',
      SBD: 'SI$',
      VUV: 'Vt',
      WST: 'WS$',
      XPF: 'F',
      TOP: 'T$',
      SZL: 'E',
      LSL: 'L',
      BWP: 'P',
      NAD: 'N$',
      MZN: 'MT',
      AOA: 'Kz',
      MWK: 'MK',
      TZS: 'TSh',
      UGX: 'USh',
      RWF: 'RF',
      ETB: 'Br',
      DJF: 'Fdj',
      SOS: 'S',
      ERN: 'Nfk',
      SDG: 'ج.س.',
      SSP: '£',
      LYD: 'ل.د',
      TND: 'د.ت',
      DZD: 'د.ج',
      MAD: 'د.م.',
      MUR: '₨',
      SCR: 'SR',
      MVR: 'Rf',
      BIF: 'Fr',
      CDF: 'FC',
      GMD: 'D',
      GNF: 'Fr',
      LRD: 'L$',
      SLL: 'Le',
      STN: 'Db',
      EGP: 'E£',
      NGN: '₦',
      KES: 'KSh',
      GHS: '₵',
      AED: 'د.إ',
      SAR: '﷼',
      OMR: '﷼',
      BHD: '.د.ب',
      KWD: 'د.ك',
      QAR: '﷼',
      YER: '﷼',
    };

    // Return symbol if found, otherwise return the code
    return currencyMap[currencyCode] || currencyCode;
  }

  // createProductCard is still used for the SEARCH results in the builder view
  createProductCard(product) {
    const isSelected = this.state.selectedProducts.some(
      (p) => p.id === product.id
    );
    const price = parseFloat(product.priceRange.minVariantPrice.amount).toFixed(
      2
    );
    const currencyCode = product.priceRange.minVariantPrice.currencyCode;
    const currencySymbol = this.getCurrencySymbol(currencyCode);
    const compareAtPrice = product.compareAtPriceRange?.minVariantPrice?.amount
      ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount).toFixed(
          2
        )
      : null;
    const image = product.featuredImage ? product.featuredImage.url : '';

    // Get Color Name
    const colorOption = product.options?.find(
      (o) =>
        o.name.toLowerCase() === 'color' || o.name.toLowerCase() === 'colour'
    );
    const colorName = colorOption ? colorOption.values[0] : '';

    // Price HTML
    let priceHtml = `<div class="stl-product-price">${currencySymbol}${price}</div>`;
    if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
      priceHtml = `
        <div class="stl-product-price stl-price-container">
          <span class="stl-compare-price">${currencySymbol}${compareAtPrice}</span>
          <span class="stl-sale-price">${currencySymbol}${price}</span>
        </div>
      `;
    }

    return `
      <div class="stl-product-card ${isSelected ? 'selected' : ''}" data-id="${
      product.id
    }">
        <div class="product-card__media-container">
          <img src="${image}" alt="${
      product.title
    }" class="stl-product-image" loading="lazy">
        </div>
        
        <div class="stl-product-info">
          <div class="stl-product-title">${product.title}</div>
          ${
            product.productType
              ? `<div class="stl-product-subtitle">${product.productType}</div>`
              : ''
          }
          ${
            colorName ? `<div class="stl-product-color">${colorName}</div>` : ''
          }
          ${priceHtml}
        </div>
      </div>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.share-the-look-section');
  if (container) {
    new ShareTheLook(container);
  }
});
